import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import { useCallback } from "react";

import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { modifiersRouter } from "@editor/trpc/modifiers";
import { nextJSProjectUtils } from "@editor/trpc/nextJSProject";
import { webContainerClient } from "@editor/trpc/webContainer";

import { useNextDevServer } from "@/features/code-execution/context";
import { useSearchParams } from "react-router-dom";
import { ReploError } from "schemas/errors";

import { useIsChatLoading } from "../../chat/store/ChatStoreContext";
import { useModifiersStoreActions } from "../store/ModifiersStoreContext";

class ModifierParameterError extends ReploError {}

export const useModifiers = (): {
  applyModifier: (
    modifier: UniversalModifierValue,
    byteOffset: number,
  ) => Promise<void>;
  isLoading: boolean;
} => {
  const projectId = useCurrentProjectId();
  const [searchParams] = useSearchParams();
  const pageId = searchParams.get("pageId");
  const chatId = searchParams.get("chatId");
  const { mountAdditionalFiles } = useNextDevServer();
  const isChatLoading = useIsChatLoading(chatId);
  const modifierActions = useModifiersStoreActions();

  const applyModifierMutation = modifiersRouter.modify.useMutation({
    onSuccess: async (result, variables) => {
      if (mountAdditionalFiles && result.fileTree) {
        try {
          await mountAdditionalFiles(result.fileTree);
        } catch (webContainerError) {
          console.warn(
            "Web container file tree mounting failed (non-critical):",
            webContainerError,
          );
        }
      }

      /**
       * NOTE (Kurt, 2025-08-25): For font family changes, we need to update
       * globals.css separately to include the new font definitions. This
       * ensures the new font is available immediately without requiring a
       * full container reload.
       */
      if (variables.modifier.type === "fontFamily") {
        try {
          const globalsFileTree =
            await webContainerClient.getGlobalsCssFileTree.query({
              projectId: variables.projectId,
            });
          // TODO (Gabe 2025-09-08): Need to ensure CodeSandbox has access to
          // this update.
          await mountAdditionalFiles?.(globalsFileTree);
        } catch (error) {
          console.warn("Failed to mount globals CSS file tree:", error);
        }
      }
    },
  });

  const applyModifier = useCallback(
    async (modifier: UniversalModifierValue, elementByteOffset: number) => {
      if (!projectId || !pageId) {
        throw new ModifierParameterError({
          message: "Missing required parameters for modifier",
          additionalData: {
            projectId,
            pageId,
          },
        });
      }

      await applyModifierMutation.mutateAsync({
        projectId,
        fileId: pageId,
        elementSelector: {
          byteOffset: elementByteOffset,
        },
        modifier,
      });

      if (projectId) {
        // NOTE (Kurt, 2025-07-21): Invalidate page component so that things
        // that rely on its lastPublishedFileVersionId etc, are updated
        await nextJSProjectUtils.getPage.invalidate({
          projectId,
          id: pageId,
        });
      }

      if (modifier.type === "delete") {
        modifierActions.resetToDefaults();
        modifierActions.setDisplayMode("select");
      }
    },
    [projectId, pageId, applyModifierMutation, modifierActions],
  );

  return {
    applyModifier,
    isLoading: isChatLoading,
  };
};
